/* eslint-disable no-restricted-imports, react/jsx-no-bind */
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import { PencilAlt } from '@styled-icons/heroicons-outline/PencilAlt'
import { useFlags } from 'launchdarkly-react-client-sdk'
import noop from 'lodash/noop'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'

import { useLazyGetAllCSNotesQuery } from 'api/notes/configuredServiceNotesCrud'
import { ReactComponent as ServiceNoteIcon } from 'assets/note/ServiceNote.svg'
import { ReactComponent as RentalFeeGracePeriodIcon } from 'assets/RentalFeeGracePeriod.svg'
import ReactivateServiceMenuItem from 'components/customer-details/tables/ConfiguredServices/ReactivateServiceMenuItem'
import ViewArchivedNotesMenuOption from 'components/notes/common/actions/ViewArchivedNotesMenuOption'
import AddUpdateCSNoteDialog from 'components/notes/configured-service/AddUpdateCSNoteDialog'
import ViewArchiveNotesDialog from 'components/notes/configured-service/ViewArchiveNotesDialog'
import RentalFeeBadge from 'components/pricing/RentalFees/common/badge/RentalFeeBadge'
import RentalFeesDialog from 'components/pricing/RentalFees/ConfiguredServices/RentalFeesDialog'
import RentalFeesDialogV2 from 'components/pricing/RentalFees/ConfiguredServices/RentalFeesDialogV2'
import UnbilledDaysDialog from 'components/pricing/RentalFees/UnbilledDays/UnbilledDaysDialog'
import { isRentalFeeFeatureAvailable } from 'data/launch-darkly/featureSelectors'
import { getSortedNotes, getArchivedAndUnarchivedNotes } from 'data/notes/notesSelectors'
import { getConfiguredServiceDetailsPageUrl } from 'router/routes'
import T from 'T'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'

const ConFiguredServiceActionMenu = ({
  anchorEl,
  selectedService,
  canEdit = false,
  locationId = '',
  accountId = '',
  onEditService = noop,
  onClose,
  useRentalFeesV2 = false,
}) => {
  const navigate = useNavigate()
  const flags = useFlags()
  const isRentalFeeEnabled = isRentalFeeFeatureAvailable({ flags })
  const [getAllCSNotes, { isFetching: isGetAllNotesLoading, data: notesData }] = useLazyGetAllCSNotesQuery()
  const [isOpenViewArchiveNoteDialog, setIsOpenViewArchiveNoteDialog] = useState(false)
  const [isOpenAddUpdateNoteDialog, setIsOpenAddUpdateNoteDialog] = useState(false)
  const [isOpenRentalFeesDialog, setIsOpenRentalFeesDialog] = useState(false)
  const [isOpenUnbilledDaysDialog, setIsOpenUnbilledDaysDialog] = useState(false)

  const sortedNotes = getSortedNotes({ notes: get(notesData, 'notes', []) })
  const { archivedNotes } = getArchivedAndUnarchivedNotes({ notes: sortedNotes })
  const configuredServiceId = get(selectedService, 'configuredServiceId') || get(selectedService, 'id')
  const archivedNotesCount = get(selectedService, 'archivedNoteCount', 0)
  const rentalFeeCount = get(selectedService, 'rentalFeeCount', 0)

  const handleViewArchiveClick = () => {
    onClose()
    setIsOpenViewArchiveNoteDialog(true)
    getAllCSNotes({ id: configuredServiceId })
      .unwrap()
      .catch(error => {
        handleError(error)
        setIsOpenViewArchiveNoteDialog(false)
      })
  }

  const handleCloseViewArchiveDialog = () => setIsOpenViewArchiveNoteDialog(false)

  const handleOpenAddNoteDialog = () => {
    onClose()
    setIsOpenAddUpdateNoteDialog(true)
  }

  const handleCloseAddNote = () => setIsOpenAddUpdateNoteDialog(false)

  const handleOpenRentalFeesDialog = () => {
    setIsOpenRentalFeesDialog(true)
    onClose()
  }

  const handleCloseRentalFeesDialog = () => {
    setIsOpenRentalFeesDialog(false)
  }

  const handleOpenUnbilledDaysDialog = () => {
    setIsOpenUnbilledDaysDialog(true)
    onClose()
  }

  const handleCloseUnbilledDaysDialog = () => {
    setIsOpenUnbilledDaysDialog(false)
  }

  const handleEditService = () => {
    onEditService()
    onClose()
  }

  const handleGoToServicePage = () => {
    const baseUrl = getConfiguredServiceDetailsPageUrl(configuredServiceId)
    const params = new URLSearchParams({ locationId, accountId }).toString()
    navigate(`${baseUrl}?${params}`)
    onClose()
  }

  return (
    <>
      <Menu
        disableScrollLock
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={Boolean(anchorEl)}
        slotProps={{ paper: { variant: 'outlined' } }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={onClose}
      >
        {canEdit && (
          <MenuItem onClick={handleEditService}>
            <ListItemIcon sx={{ mr: 1 }}>
              <PencilAlt height={24} width={24} />
            </ListItemIcon>
            <ListItemText>{T.EDIT_SERVICE}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleGoToServicePage}>
          <ListItemIcon sx={{ mr: 1 }}>
            <OpenInNewIcon />
          </ListItemIcon>
          <ListItemText>{T.GO_TO_SERVICE_PAGE}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleOpenAddNoteDialog}>
          <ListItemIcon sx={{ mr: 1 }}>
            <ServiceNoteIcon />
          </ListItemIcon>
          <ListItemText>{T.ADD_NOTE}</ListItemText>
        </MenuItem>
        {isRentalFeeEnabled && (
          <>
            <MenuItem onClick={handleOpenRentalFeesDialog}>
              <ListItemIcon sx={{ mr: 1 }}>
                <RentalFeeBadge rentalFeeCount={rentalFeeCount} />
              </ListItemIcon>
              <ListItemText>{T.RENTAL_FEES}</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleOpenUnbilledDaysDialog}>
              <ListItemIcon sx={{ mr: 1 }}>
                <RentalFeeGracePeriodIcon />
              </ListItemIcon>
              <ListItemText>{T.UNBILLED_DAYS}</ListItemText>
            </MenuItem>
          </>
        )}
        <ViewArchivedNotesMenuOption count={archivedNotesCount} isDisabled={archivedNotesCount === 0} onClick={handleViewArchiveClick} />
        {/* Mounted with the open menu so one service's details are read, not every row's. */}
        {Boolean(anchorEl) && configuredServiceId && (
          <ReactivateServiceMenuItem configuredServiceId={configuredServiceId} onDone={onClose} />
        )}
      </Menu>
      <AddUpdateCSNoteDialog
        id={configuredServiceId}
        isOpen={isOpenAddUpdateNoteDialog}
        title={T.NEW_CONFIGURED_SERVICE_NOTE}
        onClose={handleCloseAddNote}
      />
      <ViewArchiveNotesDialog
        id={configuredServiceId}
        isLoading={isGetAllNotesLoading}
        isOpen={isOpenViewArchiveNoteDialog}
        notes={archivedNotes}
        onClose={handleCloseViewArchiveDialog}
      />
      {useRentalFeesV2 ? (
        <RentalFeesDialogV2
          configuredServiceId={configuredServiceId}
          isOpen={isOpenRentalFeesDialog}
          onClose={handleCloseRentalFeesDialog}
        />
      ) : (
        <RentalFeesDialog configuredServiceId={configuredServiceId} isOpen={isOpenRentalFeesDialog} onClose={handleCloseRentalFeesDialog} />
      )}
      <UnbilledDaysDialog
        configuredServiceId={configuredServiceId}
        isOpen={isOpenUnbilledDaysDialog}
        onClose={handleCloseUnbilledDaysDialog}
      />
    </>
  )
}

ConFiguredServiceActionMenu.propTypes = {
  anchorEl: PropTypes.object,
  selectedService: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  canEdit: PropTypes.bool,
  locationId: PropTypes.string,
  accountId: PropTypes.string,
  onEditService: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  useRentalFeesV2: PropTypes.bool,
}

export default ConFiguredServiceActionMenu
